import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, RefreshCw, Shield, Key } from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';
import { useKitchenAuth } from '../context/KitchenAuthContext';

const ROLES = [
  { value: 'super_admin',        label: 'Super Admin',      icon: '🛡️', color: '#8B5CF6', desc: 'Full access to all features' },
  { value: 'admin',              label: 'Admin',            icon: '👤', color: '#3B82F6', desc: 'Manage orders, menu and reports' },
  { value: 'kitchen_manager',    label: 'Kitchen Manager',  icon: '👨‍🍳', color: '#FF6B35', desc: 'Manage orders and kitchen operations' },
  { value: 'finance_manager',    label: 'Finance Manager',  icon: '💰', color: '#10B981', desc: 'View financial reports and payouts' },
  { value: 'support_executive',  label: 'Support Exec',     icon: '🎧', color: '#64748B', desc: 'Handle customer queries' },
];
const roleMeta = (v) => ROLES.find(r => r.value === v) || { label: v, icon: '👤', color: '#64748B', desc: '' };

const EMPTY_FORM = { full_name: '', username: '', email: '', phone: '', role: 'kitchen_manager', password: '', is_active: true };

export default function TeamPage() {
  const { can } = useKitchenAuth();
  const [users,    setUsers]   = useState([]);
  const [loading,  setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form,     setForm]    = useState(EMPTY_FORM);
  const [saving,   setSaving]  = useState(false);
  const [formErr,  setFormErr] = useState({});
  const [showReset, setShowReset] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPw,    setNewPw]   = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetErr, setResetErr] = useState('');
  const [toast,    setToast]   = useState('');
  const [toastType, setToastType] = useState('success');
  const [search,   setSearch]  = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const showMsg = (msg, type = 'success') => { setToast(msg); setToastType(type); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const r = await kitchenApi.getKitchenUsers();
    if (r.success) setUsers(Array.isArray(r.data) ? r.data : (r.data?.users || []));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditUser(null);
    setFormErr({});
    setShowForm(true);
  };

  const openEdit = (user) => {
    setForm({
      full_name: user.full_name || '',
      username:  user.username  || '',
      email:     user.email     || '',
      phone:     user.phone     || '',
      role:      user.role      || 'kitchen_manager',
      password:  '',
      is_active: user.is_active ?? true,
    });
    setEditUser(user);
    setFormErr({});
    setShowForm(true);
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateForm = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Required';
    if (!form.username.trim())  e.username  = 'Required';
    if (!editUser && !form.password) e.password = 'Required for new users';
    if (!form.role) e.role = 'Select a role';
    return e;
  };

  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) { setFormErr(errs); return; }
    setSaving(true);
    const payload = { ...form };
    if (!payload.password) delete payload.password; // don't send empty pw on edit
    const res = editUser
      ? await kitchenApi.updateKitchenUser(editUser.kitchen_user_id || editUser.id, payload)
      : await kitchenApi.createKitchenUser(payload);
    setSaving(false);
    if (res.success) {
      setShowForm(false);
      load();
      showMsg(editUser ? 'User updated!' : 'User created!');
    } else {
      setFormErr({ submit: res.error || 'Failed to save.' });
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Remove ${user.full_name || user.username}?`)) return;
    const res = await kitchenApi.deleteKitchenUser(user.kitchen_user_id || user.id);
    if (res.success) { load(); showMsg('User removed.'); }
    else showMsg(res.error || 'Failed to remove user.', 'error');
  };

  const handleToggleStatus = async (user) => {
    const res = await kitchenApi.toggleKitchenUserStatus(user.kitchen_user_id || user.id, !user.is_active);
    if (res.success) {
      setUsers(prev => prev.map(u => (u.kitchen_user_id || u.id) === (user.kitchen_user_id || user.id) ? { ...u, is_active: !u.is_active } : u));
      showMsg(`User ${!user.is_active ? 'activated' : 'deactivated'}.`);
    }
  };

  const openResetPw = (user) => {
    setResetUser(user);
    setNewPw(''); setConfirmPw(''); setResetErr('');
    setShowReset(true);
  };

  const handleResetPw = async () => {
    if (newPw.length < 6) { setResetErr('Password must be at least 6 characters.'); return; }
    if (newPw !== confirmPw) { setResetErr('Passwords do not match.'); return; }
    setResetSaving(true);
    const res = await kitchenApi.resetKitchenUserPassword(resetUser.kitchen_user_id || resetUser.id, newPw);
    setResetSaving(false);
    if (res.success) { setShowReset(false); showMsg('Password reset successfully!'); }
    else setResetErr(res.error || 'Failed to reset password.');
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || (u.full_name || u.username || '').toLowerCase().includes(search.toLowerCase()) || (u.email || '').toLowerCase().includes(search.toLowerCase());
    const matchRole   = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  if (!can.team) return (
    <div className="k-card"><div className="k-empty"><div className="k-empty-icon">🔒</div><div className="k-empty-title">Access Restricted</div><div className="k-empty-sub">You don't have permission to manage team members.</div></div></div>
  );

  return (
    <div>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Team Management</h1>
          <p className="k-page-sub">{users.length} team member{users.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="k-page-actions">
          <button className="k-btn k-btn-primary" onClick={openAdd}><Plus size={16} /> Add Member</button>
          <button className="k-btn-icon" onClick={load} title="Refresh"><RefreshCw size={16} /></button>
        </div>
      </div>

      {/* Filters */}
      <div className="k-card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="k-input-group" style={{ flex: '1 1 220px' }}>
            <span className="k-input-icon" style={{ left: 12, top: '50%', transform: 'translateY(-50%)', position: 'absolute' }}>🔍</span>
            <input className="k-input" style={{ paddingLeft: 38 }} placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="k-select" style={{ maxWidth: 200 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="all">All Roles</option>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {/* Users */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {Array(4).fill(0).map((_, i) => <div key={i} className="k-card"><div className="k-skeleton" style={{ height: 100 }} /></div>)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="k-card">
          <div className="k-empty">
            <div className="k-empty-icon">👥</div>
            <div className="k-empty-title">{users.length === 0 ? 'No team members yet' : 'No members match your filters'}</div>
            {users.length === 0 && <button className="k-btn k-btn-primary" style={{ marginTop: 16 }} onClick={openAdd}><Plus size={16} /> Add First Member</button>}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filtered.map(user => {
            const rm = roleMeta(user.role);
            return (
              <div key={user.kitchen_user_id || user.id} className="k-card" style={{ opacity: user.is_active ? 1 : .6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div className="k-avatar" style={{ background: `${rm.color}22`, borderColor: rm.color, color: rm.color }}>
                    {(user.full_name || user.username || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{user.full_name || user.username}</div>
                    <div style={{ fontSize: 12, color: 'var(--k-text-sub)' }}>{user.email}</div>
                  </div>
                  <span className={`k-badge ${user.is_active ? 'k-badge-active' : 'k-badge-inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <span style={{ fontSize: 14 }}>{rm.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: rm.color }}>{rm.label}</span>
                </div>

                {user.phone && <div style={{ fontSize: 12, color: 'var(--k-text-sub)', marginBottom: 12 }}>📱 {user.phone}</div>}

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px solid var(--k-border-light)', paddingTop: 12 }}>
                  <button className="k-btn k-btn-sm k-btn-ghost" style={{ flex: 1 }} onClick={() => openEdit(user)}>
                    <Edit2 size={13} /> Edit
                  </button>
                  <button
                    className="k-btn k-btn-sm k-btn-ghost"
                    style={{ flex: 1, color: user.is_active ? 'var(--k-warning)' : 'var(--k-success)' }}
                    onClick={() => handleToggleStatus(user)}
                  >
                    {user.is_active ? '⏸ Deactivate' : '▶ Activate'}
                  </button>
                  <button className="k-btn-icon" style={{ padding: 6 }} onClick={() => openResetPw(user)} title="Reset password">
                    <Key size={14} />
                  </button>
                  <button className="k-btn-icon" style={{ padding: 6, color: 'var(--k-danger)' }} onClick={() => handleDelete(user)} title="Remove">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="k-modal-overlay">
          <div className="k-modal k-modal-md">
            <div className="k-modal-header">
              <div className="k-modal-title">{editUser ? 'Edit Team Member' : 'Add Team Member'}</div>
              <button className="k-btn-icon" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <div className="k-modal-body">
              <div className="k-grid-2">
                <div className="k-form-group">
                  <label className="k-label required">Full Name</label>
                  <input className={`k-input${formErr.full_name ? ' k-input-error' : ''}`} value={form.full_name} onChange={e => setF('full_name', e.target.value)} />
                  {formErr.full_name && <div className="k-error-text">{formErr.full_name}</div>}
                </div>
                <div className="k-form-group">
                  <label className="k-label required">Username</label>
                  <input className={`k-input${formErr.username ? ' k-input-error' : ''}`} value={form.username} onChange={e => setF('username', e.target.value)} />
                  {formErr.username && <div className="k-error-text">{formErr.username}</div>}
                </div>
              </div>
              <div className="k-grid-2">
                <div className="k-form-group">
                  <label className="k-label">Email</label>
                  <input className="k-input" type="email" value={form.email} onChange={e => setF('email', e.target.value)} />
                </div>
                <div className="k-form-group">
                  <label className="k-label">Phone</label>
                  <input className="k-input" value={form.phone} onChange={e => setF('phone', e.target.value)} />
                </div>
              </div>
              <div className="k-form-group">
                <label className="k-label required">Role</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {ROLES.map(r => (
                    <button
                      key={r.value}
                      type="button"
                      style={{
                        border: `2px solid ${form.role === r.value ? r.color : 'var(--k-border)'}`,
                        background: form.role === r.value ? `${r.color}12` : 'var(--k-surface)',
                        borderRadius: 10, padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
                      }}
                      onClick={() => setF('role', r.value)}
                    >
                      <div style={{ fontSize: 14, marginBottom: 2 }}>{r.icon} <strong style={{ color: form.role === r.value ? r.color : 'var(--k-text)' }}>{r.label}</strong></div>
                      <div style={{ fontSize: 11, color: 'var(--k-text-sub)' }}>{r.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="k-form-group">
                <label className="k-label">{editUser ? 'New Password (leave blank to keep)' : 'Password'}</label>
                <input
                  className={`k-input${formErr.password ? ' k-input-error' : ''}`}
                  type="password"
                  placeholder={editUser ? 'Leave blank to keep current' : 'Set initial password'}
                  value={form.password}
                  onChange={e => setF('password', e.target.value)}
                />
                {formErr.password && <div className="k-error-text">{formErr.password}</div>}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setF('is_active', e.target.checked)} style={{ accentColor: 'var(--k-primary)', width: 16, height: 16 }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Active (can log in)</span>
              </label>
              {formErr.submit && <div className="k-error-text" style={{ marginTop: 12 }}>{formErr.submit}</div>}
            </div>
            <div className="k-modal-footer">
              <button className="k-btn k-btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="k-btn k-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> : (editUser ? 'Save Changes' : 'Add Member')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showReset && (
        <div className="k-modal-overlay">
          <div className="k-modal k-modal-sm">
            <div className="k-modal-header">
              <div className="k-modal-title">Reset Password</div>
              <button className="k-btn-icon" onClick={() => setShowReset(false)}><X size={18} /></button>
            </div>
            <div className="k-modal-body">
              <p style={{ fontSize: 14, color: 'var(--k-text-sub)', marginBottom: 16 }}>
                Resetting password for <strong>{resetUser?.full_name || resetUser?.username}</strong>
              </p>
              <div className="k-form-group">
                <label className="k-label required">New Password</label>
                <input className="k-input" type="password" placeholder="Min 6 characters" value={newPw} onChange={e => setNewPw(e.target.value)} />
              </div>
              <div className="k-form-group">
                <label className="k-label required">Confirm Password</label>
                <input className="k-input" type="password" placeholder="Confirm new password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
              </div>
              {resetErr && <div className="k-error-text">{resetErr}</div>}
            </div>
            <div className="k-modal-footer">
              <button className="k-btn k-btn-ghost" onClick={() => setShowReset(false)}>Cancel</button>
              <button className="k-btn k-btn-primary" onClick={handleResetPw} disabled={resetSaving}>
                {resetSaving ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="k-toast-container">
          <div className={`k-toast ${toastType}`}>{toastType === 'success' ? '✓' : '✕'} {toast}</div>
        </div>
      )}
    </div>
  );
}
